import { faChain, faChainBroken, faRefresh, faTrash, faUpload, faProcedures } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Card, Checkbox, Col, Divider, Row, Space, Tooltip, message, theme, Typography, Badge, Tag } from 'antd'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { handleError, hexToRGBA } from '../../lib/utils'
import { createProcedurePerformed } from '../examination-utils/examination-procedures-utils'
import dayjs from 'dayjs'
import { readProcedures } from '../../Admin/entities/utils/utils'
import { deleteFile, getFile, uploadFile } from '../../Admin/config/utils/utils'
import {
  countBillingRecords,
  createBillingRecord,
  getProcedurePrice,
  getProductPrice
} from '../../Bookings/utils/utils'
import CustomLoadingIcon from '../../reusable-components/CustomLoadingIcon'
import { useProcedures } from '../examination-utils/ProceduresContext'
import { GlobalServicesEnums } from '../../Super-Admin/Global Services/utils/enums'
import store from '../../../store'

const { Title, Text } = Typography

// Constants
const SYMPTOM_TAG_COLOR = '#FF69B4' // Hot pink color for badges

const ExaminationProceduresPerformed = ({ bookingData, userRef }) => {
  const state = useSelector((state) => state)
  const [webSocket, setWebSocket] = useState({
    connected: false,
    socket: null
  })

  // Get shared state from context
  const { proceduresPerformed, setProceduresPerformed, proceduresPerformedLoading, fetchProceduresPerformed } =
    useProcedures()

  // Procedures Performed
  const [procedureList, setProcedureList] = useState([])
  const [keywordProcedures, setKeywordProcedures] = useState([])

  const { token } = theme.useToken()

  // Helper function to get category info from enum
  const getCategoryInfo = (categoryValue) => {
    if (!categoryValue) return null
    const categoryInfo = GlobalServicesEnums.categories.find((cat) => cat.value === categoryValue)
    return categoryInfo || { label: categoryValue, color: '#d9d9d9', icon: '📋' }
  }

  useEffect(() => {
    const proceduresPermormedSocket = new WebSocket(`${process.env.REACT_APP_NODE_RED_WS_URL}/procedures-performed`)

    proceduresPermormedSocket.onopen = () => {
      setWebSocket({
        connected: true,
        socket: proceduresPermormedSocket
      })
    }

    proceduresPermormedSocket.onerror = (error) => {
      handleError(error, true)
    }

    proceduresPermormedSocket.onmessage = (event) => {
      if (JSON.parse(event.data).bookingRef === bookingData._id) {
        setProceduresPerformed(JSON.parse(event.data).data)
      }
    }

    return () => {
      proceduresPermormedSocket.close()

      setWebSocket({
        connected: true,
        socket: proceduresPermormedSocket
      })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const clinicServices = state.clinics.data
      .find((i) => i._id === state.auth.agiliteUser.extraData.clinics[0])
      .servicesConfig.filter((i) => i.name && i.procedureShortcut)

    setProcedureList(clinicServices)
    handleGetKeywordProcedures()
    // eslint-disable-next-line
  }, [])

  const handleGetKeywordProcedures = async () => {
    try {
      let tmpData = []
      tmpData = await readProcedures()
      setKeywordProcedures(tmpData)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleProcedureChange = async (key, value, fileRef, fileName) => {
    try {
      const tmpProceduresPerformed = [...proceduresPerformed]

      console.log('Procedures Performed', tmpProceduresPerformed)

      let currentProcedureIndex = tmpProceduresPerformed.findIndex((i) => i.key === key)

      createProcedurePerformed({
        bookingRef: bookingData._id,
        createdBy: state.auth.agiliteUser.firstName + ' ' + state.auth.agiliteUser.lastName,
        dateCreated: dayjs(new Date()),
        fileRef,
        fileName,
        key,
        value,
        userRef
      })

      if (currentProcedureIndex !== -1) {
        tmpProceduresPerformed[currentProcedureIndex].value = value
        tmpProceduresPerformed[currentProcedureIndex].fileRef = fileRef
        tmpProceduresPerformed[currentProcedureIndex].fileName = fileName
      } else {
        tmpProceduresPerformed.push({
          key,
          value,
          fileRef,
          fileName
        })
      }

      // Update shared context
      setProceduresPerformed(tmpProceduresPerformed)

      // Also send through websocket for real-time updates
      if (webSocket.socket && webSocket.connected) {
        webSocket.socket.send(JSON.stringify({ bookingRef: bookingData._id, data: tmpProceduresPerformed }))
      }

      // Billing Macros
      const tmpBillingItems = []
      let tmpMacroIndex = -1

      if (bookingData.clinicRecord?.servicesConfig) {
        tmpMacroIndex = bookingData.clinicRecord.servicesConfig.findIndex((i) => i.name === key)
      }
      if (tmpMacroIndex !== -1 && value) {
        if (bookingData.clinicRecord.servicesConfig[tmpMacroIndex].billingMacroItems) {
          bookingData.clinicRecord.servicesConfig[tmpMacroIndex].billingMacroItems.forEach((item) => {
            tmpBillingItems.push({
              code: item.code,
              name: item.name,
              qty: item.qty,
              type: item.type,
              bookingRef: bookingData._id,
              procedureKey: key,
              icd10: bookingData.diagnosis.map((i) => i.code),
              medicalAid: '0',
              patient: '0',
              total: '0',
              privatePrice: item.privatePrice,
              packSize: undefined,
              nappiCode: undefined
            })
          })
        }
      }

      for (const item of tmpBillingItems) {
        if (item.type === 'Medicine' || item.type === 'Material') {
          try {
            let priceResponse = null

            if (
              !bookingData.patientRecord.medicalAid.name &&
              (item.privatePrice !== '0' || item.privatePrice.trim() !== '')
            ) {
              item.medicalAid = '0'
              item.patient = item.privatePrice
              item.total = item.privatePrice
            } else {
              priceResponse = await getProductPrice(
                item.type.toLowerCase(),
                item.code,
                bookingData.patientRecord.medicalAid.planOptionCode || '95C'
              )
            }

            item.medicalAid = (priceResponse.price - priceResponse.patientLiable).toFixed(2)
            item.patient = priceResponse.patientLiable
            item.total = priceResponse.price
          } catch (e) {}
        }

        if (item.type === 'Procedure' || item.type === 'Consultation') {
          let isContract = false

          if (
            bookingData.clinicRecord.billingProfile.schemeContracts.includes(
              bookingData.patientRecord.medicalAid.schemeCode
            )
          ) {
            isContract = true
          }

          try {
            let priceResponse = null

            if (
              !bookingData.patientRecord.medicalAid.name &&
              (item.privatePrice !== '0' || item.privatePrice.trim() !== '')
            ) {
              item.medicalAid = '0'
              item.patient = item.privatePrice
              item.total = item.privatePrice
            } else {
              priceResponse = await getProcedurePrice(
                item.code,
                bookingData.patientRecord.medicalAid.planOptionCode || '95C',
                isContract,
                bookingData.clinicRecord.billingProfile
              )
            }

            item.medicalAid = priceResponse.price
            item.patient = 0
            item.total = priceResponse.price
          } catch (e) {}
        }
      }

      const recordCount = await countBillingRecords({ bookingRef: bookingData._id, procedureKey: key })

      if (recordCount.result === 0) {
        createBillingRecord(tmpBillingItems)
      }
    } catch (e) {
      console.log('Error creating billing record', e)
      message.error(handleError(e, true))
    }
  }

  const checkValue = (key) => {
    let returnValue = false

    if (proceduresPerformed.length > 0) {
      const procedureEntry = proceduresPerformed.find((i) => i.key === key)

      if (procedureEntry) {
        returnValue = procedureEntry.value
      } else {
        returnValue = false
      }
    } else {
      returnValue = false
    }

    return returnValue
  }

  const hasFileUploaded = (procName) => {
    const procedureEntry = proceduresPerformed.find((i) => i.key === procName)
    return procedureEntry && procedureEntry.fileName && procedureEntry.fileRef
  }

  const shouldShowFileUpload = (procName) => {
    const tmpIndex = keywordProcedures.findIndex((i) => i.label === procName)
    return tmpIndex !== -1 && keywordProcedures[tmpIndex].value === 'true'
  }

  const handleFileUpload = async (name, file) => {
    let response = null
    try {
      response = await uploadFile(name, 'application/octet-stream', file, true)
      return response
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleFileDownload = async (fileRef, fileName) => {
    try {
      const fileResponse = await getFile(fileRef)
      const link = document.createElement('a')
      const blob = new Blob([fileResponse])
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleFileDelete = (procName, fileRef) => {
    deleteFile(fileRef)
    handleProcedureChange(procName, true, '', '')
  }

  // Count completed procedures
  const completedProceduresCount = proceduresPerformed.filter((proc) => proc.value === true).length

  // Render a single procedure item with enhanced one-line layout
  const renderProcedureItem = (procedureConfig, index) => {
    const procName = procedureConfig.name
    const isChecked = checkValue(procName)
    const hasFile = hasFileUploaded(procName)
    const showFileUpload = shouldShowFileUpload(procName)
    const categoryInfo = getCategoryInfo(procedureConfig.category)

    return (
      <div
        key={procName}
        style={{
          marginBottom: '12px',
          padding: '12px 16px',
          background: isChecked ? hexToRGBA(token.colorPrimary, 0.04) : token.colorBgContainer,
          border: `1px solid ${isChecked ? hexToRGBA(token.colorPrimary, 0.2) : token.colorBorder}`,
          borderRadius: '8px',
          transition: 'all 0.2s ease'
        }}
      >
        <Row align='middle' justify='space-between'>
          {/* Left side - Checkbox and procedure info */}
          <Col flex='auto'>
            <Row align='middle' gutter={[12, 0]}>
              <Col>
                <Checkbox
                  onChange={(e) => handleProcedureChange(procName, e.target.checked)}
                  checked={isChecked}
                  style={{
                    transform: 'scale(1.1)',
                    marginRight: '8px'
                  }}
                />
              </Col>
              <Col flex='auto'>
                <div>
                  <Text
                    strong
                    style={{
                      fontSize: '15px',
                      color: isChecked ? token.colorPrimary : token.colorText
                    }}
                  >
                    {procName}
                  </Text>
                  <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {categoryInfo && (
                      <Tag
                        color={categoryInfo.color}
                        style={{
                          fontSize: '11px',
                          marginRight: 0,
                          borderRadius: '10px',
                          padding: '2px 8px',
                          border: 'none'
                        }}
                      >
                        <span style={{ marginRight: '4px' }}>{categoryInfo.icon}</span>
                        {categoryInfo.label}
                      </Tag>
                    )}
                    {procedureConfig.durationMin && (
                      <Text
                        style={{
                          fontSize: '12px',
                          color: token.colorTextSecondary,
                          fontWeight: 500
                        }}
                      >
                        ⏱️ {procedureConfig.durationMin} min
                      </Text>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>

          {/* Right side - File upload actions */}
          {showFileUpload && (
            <Col>
              <Space size='small'>
                {hasFile ? (
                  <>
                    <Tooltip title={`Download: ${proceduresPerformed.find((p) => p.key === procName)?.fileName}`}>
                      <Button
                        type='text'
                        size='small'
                        onClick={() => {
                          const proc = proceduresPerformed.find((p) => p.key === procName)
                          handleFileDownload(proc.fileRef, proc.fileName)
                        }}
                        style={{
                          color: token.colorPrimary,
                          border: `1px solid ${token.colorPrimary}`,
                          borderRadius: '6px'
                        }}
                      >
                        <FontAwesomeIcon icon={faUpload} style={{ marginRight: '4px' }} />
                        <Text style={{ fontSize: '11px', color: token.colorPrimary }}>
                          {proceduresPerformed.find((p) => p.key === procName)?.fileName?.slice(0, 15)}
                          {proceduresPerformed.find((p) => p.key === procName)?.fileName?.length > 15 ? '...' : ''}
                        </Text>
                      </Button>
                    </Tooltip>
                    <Tooltip title='Delete file'>
                      <Button
                        type='text'
                        size='small'
                        danger
                        onClick={() => {
                          const proc = proceduresPerformed.find((p) => p.key === procName)
                          handleFileDelete(procName, proc.fileRef)
                        }}
                        style={{
                          borderRadius: '6px'
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title='Upload file'>
                    <Button
                      type='text'
                      size='small'
                      onClick={() => document.getElementById(`upload-file-${procName}`).click()}
                      style={{
                        color: token.colorTextSecondary,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: '6px'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{
                          color: token.colorTextSecondary,
                          marginRight: '4px'
                        }}
                      />
                      <Text style={{ fontSize: '11px', color: token.colorTextSecondary }}>Upload</Text>
                    </Button>
                  </Tooltip>
                )}
                <input
                  id={`upload-file-${procName}`}
                  style={{ display: 'none' }}
                  type='file'
                  multiple={false}
                  onChange={async (e) => {
                    try {
                      const fileReader = new FileReader()

                      fileReader.onload = async (event) => {
                        try {
                          const arrayBuffer = event.target.result
                          const fileResponse = await handleFileUpload(e.target.files[0].name, arrayBuffer)
                          handleProcedureChange(procName, true, fileResponse._id, e.target.files[0].name)
                        } catch (e) {
                          message.error(handleError(e, true))
                        }
                      }

                      fileReader.readAsArrayBuffer(e.target.files[0])
                    } catch (e) {
                      message.error(handleError(e, true))
                    }
                  }}
                />
              </Space>
            </Col>
          )}
        </Row>
      </div>
    )
  }

  return (
    <Card
      styles={{
        header: {
          background: hexToRGBA(token.colorPrimary, 0.08),
          borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`,
          padding: '12px 16px'
        },
        body: {
          padding: '16px'
        }
      }}
      size='small'
      title={
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <FontAwesomeIcon
              icon={faProcedures}
              style={{
                color: token.colorPrimary,
                fontSize: '16px'
              }}
            />
            <Title level={5} style={{ margin: 0 }}>
              Procedures Performed
            </Title>
            {completedProceduresCount > 0 && (
              <Badge
                count={completedProceduresCount}
                style={{
                  backgroundColor: SYMPTOM_TAG_COLOR,
                  marginLeft: 8
                }}
              />
            )}
          </Space>
          <Space>
            <div
              style={{
                height: '100%',
                padding: '4px 8px',
                background: hexToRGBA(webSocket.connected ? token.colorSuccess : token.colorError, 0.2),
                border: `1px ${webSocket.connected ? token.colorSuccess : token.colorError} solid`,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <small>Realtime Updates </small>
              <Tooltip
                title={
                  webSocket.connected
                    ? 'Connected to Realtime Update Server'
                    : 'Disconnected from Realtime Update Server'
                }
              >
                <FontAwesomeIcon
                  icon={webSocket.connected ? faChain : faChainBroken}
                  size='sm'
                  color={webSocket.connected ? token.colorSuccess : token.colorError}
                  style={{ cursor: 'help', marginLeft: '4px' }}
                />
              </Tooltip>
            </div>

            <Button
              type='primary'
              style={{ background: token.colorSuccess }}
              onClick={() => fetchProceduresPerformed()}
              size='small'
            >
              <FontAwesomeIcon icon={faRefresh} />
            </Button>
          </Space>
        </div>
      }
      style={{
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginTop: 10
      }}
    >
      <CustomLoadingIcon
        content={
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: '4px',
              padding: '8px'
            }}
          >
            {procedureList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: token.colorTextSecondary }}>
                <Text>No procedures configured</Text>
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {procedureList.map((procedureConfig, index) => renderProcedureItem(procedureConfig, index))}
              </div>
            )}
          </div>
        }
        loading={proceduresPerformedLoading}
        loadingText='Loading Procedures'
      />
    </Card>
  )
}

export default ExaminationProceduresPerformed
